// Hoisted from the GLOBAL `Math` so hot loops below reference a module-scope
// binding. A global lookup is not inline-cacheable inside a `vm` context
// (how jest isolates modules), which made these loops ~100x slower under
// test than in production. Exactly semantics-preserving: every `Math`
// member is a pure static that never reads `this`.
const MATH_ABS = Math.abs;
const MATH_MAX = Math.max;
const MATH_MIN = Math.min;
const MATH_SQRT = Math.sqrt;

const {
  getWallDirection,
  doPolygonsOverlap,
  getEdges,
  projectPolygon,
  cloneLayout,
  getFixtureWidth,
  getFixtureCenter,
  getPositionAlongWall: getFixturePositionAlongWall,
  shiftCornersAlongWall,
  isCornerShower,
} = require('./geometry');
// Maximum distance a fixture can be pushed along its wall
const MAX_PUSH_DISTANCE = 1.0; // 1 meter

/**
 * Stage 5.1: Physical-Physical Collision Resolution
 *
 * Detects and resolves collisions between fixture physical bodies.
 * For cross-wall collisions, attempts to push one fixture along its wall.
 * Same-wall collisions are rejected (should not happen with proper placement).
 *
 * @param {Object} layout - Layout with fixtures
 * @param {Object} bathroom - Bathroom data
 * @param {boolean} collectDebug - Whether to collect debug info
 * @returns {{valid: boolean, layout: Object, debug?: Object}}
 */
function resolvePhysicalCollisions(layout, bathroom, collectDebug = false) {
  let workingLayout = cloneLayout(layout);
  let maxIterations = 10; // Prevent infinite loops
  const pushAttempts = collectDebug ? [] : null;

  while (maxIterations > 0) {
    const collision = findFirstCollision(workingLayout.fixtures);
    if (!collision) {
      // No collisions - we're done
      return { valid: true, layout: workingLayout, pushAttempts };
    }

    const { fixture1, fixture2, index1, index2 } = collision;

    // Try to push one fixture to resolve the collision
    const pushResult = tryPushToResolveCollision(
      workingLayout,
      index1,
      index2,
      bathroom,
      collectDebug
    );

    if (collectDebug && pushResult.attempts) {
      pushAttempts.push(...pushResult.attempts);
    }

    if (!pushResult.success) {
      return {
        valid: false,
        layout,
        pushAttempts,
        failureReason: 'push_failed',
      };
    }

    workingLayout = pushResult.layout;
    maxIterations--;
  }

  // Exceeded max iterations - likely oscillating
  return {
    valid: false,
    layout,
    pushAttempts,
    failureReason: 'max_iterations',
  };
}

/**
 * Find the first collision between fixtures
 */
function findFirstCollision(fixtures) {
  for (let i = 0; i < fixtures.length; i++) {
    for (let j = i + 1; j < fixtures.length; j++) {
      if (
        doPolygonsOverlap(
          fixtures[i].physicalCorners,
          fixtures[j].physicalCorners
        )
      ) {
        return {
          fixture1: fixtures[i],
          fixture2: fixtures[j],
          index1: i,
          index2: j,
        };
      }
    }
  }
  return null;
}

/**
 * Try to push one of two colliding fixtures to resolve collision
 * Tries pushing each fixture and picks the one that works
 */
function tryPushToResolveCollision(
  layout,
  index1,
  index2,
  bathroom,
  collectDebug = false
) {
  const fixture1 = layout.fixtures[index1];
  const fixture2 = layout.fixtures[index2];
  const attempts = collectDebug ? [] : null;

  // Don't push centered fixtures or corner showers (sliding a corner shower
  // along one parent wall would detach it from the corner).
  const canPush1 = !fixture1.centeredOn && !isCornerShower(fixture1);
  const canPush2 = !fixture2.centeredOn && !isCornerShower(fixture2);

  // Calculate how much overlap there is and push direction
  const overlapInfo = calculateOverlapInfo(
    fixture1.physicalCorners,
    fixture2.physicalCorners,
    fixture1,
    fixture2,
    bathroom
  );

  // Try pushing fixture1
  if (canPush1) {
    const result = tryPushFixture(
      layout,
      index1,
      overlapInfo.pushDistance1,
      overlapInfo.pushDirection1,
      bathroom
    );

    const stillCollides =
      result.success &&
      hasCollisionBetween(result.layout.fixtures, index1, index2);

    if (collectDebug) {
      attempts.push({
        type: 'push_attempt',
        fixture: {
          type: fixture1.type,
          parentWallIds: fixture1.parentWallIds,
          originalCenter: getFixtureCenter(fixture1.physicalCorners),
        },
        collidingWith: {
          type: fixture2.type,
          parentWallIds: fixture2.parentWallIds,
        },
        pushDistance: overlapInfo.pushDistance1,
        pushDirection: overlapInfo.pushDirection1,
        result: result.success
          ? stillCollides
            ? 'still_collides'
            : 'success'
          : 'failed',
        reason: result.success
          ? stillCollides
            ? 'Collision not resolved'
            : null
          : result.failureReason || 'Push blocked',
        newCenter: result.success
          ? getFixtureCenter(result.layout.fixtures[index1].physicalCorners)
          : null,
      });
    }

    if (result.success && !stillCollides) {
      return { success: true, layout: result.layout, attempts };
    }
  } else if (collectDebug) {
    attempts.push({
      type: 'push_skipped',
      fixture: { type: fixture1.type, parentWallIds: fixture1.parentWallIds },
      reason: 'Fixture is centered',
    });
  }

  // Try pushing fixture2
  if (canPush2) {
    const result = tryPushFixture(
      layout,
      index2,
      overlapInfo.pushDistance2,
      overlapInfo.pushDirection2,
      bathroom
    );

    const stillCollides =
      result.success &&
      hasCollisionBetween(result.layout.fixtures, index1, index2);

    if (collectDebug) {
      attempts.push({
        type: 'push_attempt',
        fixture: {
          type: fixture2.type,
          parentWallIds: fixture2.parentWallIds,
          originalCenter: getFixtureCenter(fixture2.physicalCorners),
        },
        collidingWith: {
          type: fixture1.type,
          parentWallIds: fixture1.parentWallIds,
        },
        pushDistance: overlapInfo.pushDistance2,
        pushDirection: overlapInfo.pushDirection2,
        result: result.success
          ? stillCollides
            ? 'still_collides'
            : 'success'
          : 'failed',
        reason: result.success
          ? stillCollides
            ? 'Collision not resolved'
            : null
          : result.failureReason || 'Push blocked',
        newCenter: result.success
          ? getFixtureCenter(result.layout.fixtures[index2].physicalCorners)
          : null,
      });
    }

    if (result.success && !stillCollides) {
      return { success: true, layout: result.layout, attempts };
    }
  } else if (collectDebug) {
    attempts.push({
      type: 'push_skipped',
      fixture: { type: fixture2.type, parentWallIds: fixture2.parentWallIds },
      reason: 'Fixture is centered',
    });
  }

  return { success: false, layout, attempts };
}

/**
 * Check if two specific fixtures collide
 */
function hasCollisionBetween(fixtures, index1, index2) {
  return doPolygonsOverlap(
    fixtures[index1].physicalCorners,
    fixtures[index2].physicalCorners
  );
}

/**
 * Calculate the Minimum Translation Vector (MTV) between two overlapping polygons
 * Returns the smallest vector needed to separate the polygons
 */
function calculateMTV(poly1, poly2) {
  const edges = [...getEdges(poly1), ...getEdges(poly2)];

  let minOverlap = Infinity;
  let minAxis = null;

  for (const edge of edges) {
    // Get perpendicular axis (normalized)
    const length = MATH_SQRT(edge.x * edge.x + edge.z * edge.z);
    const axis = { x: -edge.z / length, z: edge.x / length };

    // Project both polygons onto axis
    const projection1 = projectPolygon(poly1, axis);
    const projection2 = projectPolygon(poly2, axis);

    // Check for separation
    if (
      projection1.max < projection2.min ||
      projection2.max < projection1.min
    ) {
      // No overlap on this axis - polygons don't collide
      return null;
    }

    // Calculate overlap on this axis
    const overlap = MATH_MIN(
      projection1.max - projection2.min,
      projection2.max - projection1.min
    );

    if (overlap < minOverlap) {
      minOverlap = overlap;
      // Determine direction: push poly1 away from poly2
      const center1 =
        poly1.reduce((sum, p) => sum + p.x * axis.x + p.z * axis.z, 0) /
        poly1.length;
      const center2 =
        poly2.reduce((sum, p) => sum + p.x * axis.x + p.z * axis.z, 0) /
        poly2.length;
      const direction = center1 < center2 ? -1 : 1;
      minAxis = { x: axis.x * direction, z: axis.z * direction };
    }
  }

  // If no axis was found (empty edges), return null
  if (!minAxis) {
    return null;
  }

  return {
    x: minAxis.x * minOverlap,
    z: minAxis.z * minOverlap,
    magnitude: minOverlap,
  };
}

/**
 * Compute the exact minimum translation distance to separate two overlapping
 * convex polygons by moving poly1 along a specific direction.
 *
 * Algorithm: for each SAT axis (edge normals of both polygons), compute how
 * much translation along `direction` is needed to eliminate overlap on that
 * axis. The minimum across all axes is the exact answer — clearing any single
 * axis creates a separating axis by SAT, guaranteeing full 2D separation.
 *
 * @param {Array} poly1 - Polygon being moved (array of {x, z})
 * @param {Array} poly2 - Stationary polygon (array of {x, z})
 * @param {{x: number, z: number}} direction - Normalized push direction
 * @returns {number} Minimum translation distance (0 if already separated or
 *   direction is parallel to all axes)
 */
function computeDirectionalMTV(poly1, poly2, direction) {
  const edges = [...getEdges(poly1), ...getEdges(poly2)];
  let minT = Infinity;

  for (const edge of edges) {
    // Perpendicular axis (normalized)
    const length = MATH_SQRT(edge.x * edge.x + edge.z * edge.z);
    if (length < 1e-10) continue;
    const axis = { x: -edge.z / length, z: edge.x / length };

    // Project both polygons onto this axis
    const proj1 = projectPolygon(poly1, axis);
    const proj2 = projectPolygon(poly2, axis);

    // No overlap on this axis — already separated
    if (proj1.max <= proj2.min || proj2.max <= proj1.min) {
      return 0;
    }

    // Component of push direction along this axis
    const s = direction.x * axis.x + direction.z * axis.z;

    if (MATH_ABS(s) < 1e-10) continue; // Parallel — can't separate on this axis

    // How much t along direction is needed to clear overlap on this axis:
    //   s > 0: poly1 projection moves right, need proj2.max <= proj1.min + t*s
    //          → t = (proj2.max - proj1.min) / s
    //   s < 0: poly1 projection moves left, need proj1.max + t*s <= proj2.min
    //          → t = (proj1.max - proj2.min) / |s|
    const t =
      s > 0 ? (proj2.max - proj1.min) / s : (proj1.max - proj2.min) / -s;

    if (t >= 0 && t < minT) {
      minT = t;
    }
  }

  return minT === Infinity ? 0 : minT;
}

/**
 * Calculate overlap info and push amounts for two colliding fixtures.
 * Uses directional MTV to compute the exact minimum push distance along each
 * fixture's wall direction, in both left and right directions.
 */
function calculateOverlapInfo(poly1, poly2, fixture1, fixture2, bathroom) {
  // Get wall directions for each fixture
  const wall1 = bathroom.walls.find(
    (w) => w.id === fixture1.parentWallIds?.[0]
  );
  const wall2 = bathroom.walls.find(
    (w) => w.id === fixture2.parentWallIds?.[0]
  );

  const wallDir1 = getWallDirection(wall1);
  const wallDir2 = getWallDirection(wall2);

  const buffer = 0.001; // 1mm buffer for numerical precision (was 0.02)

  // Compute exact minimum push for fixture1 in both directions along its wall
  const leftMTV1 = computeDirectionalMTV(poly1, poly2, {
    x: -wallDir1.x,
    z: -wallDir1.z,
  });
  const rightMTV1 = computeDirectionalMTV(poly1, poly2, wallDir1);
  const leftAmount1 =
    leftMTV1 > 0 ? MATH_MIN(leftMTV1 + buffer, MAX_PUSH_DISTANCE) : 0;
  const rightAmount1 =
    rightMTV1 > 0 ? MATH_MIN(rightMTV1 + buffer, MAX_PUSH_DISTANCE) : 0;

  // Compute exact minimum push for fixture2 in both directions along its wall
  const leftMTV2 = computeDirectionalMTV(poly2, poly1, {
    x: -wallDir2.x,
    z: -wallDir2.z,
  });
  const rightMTV2 = computeDirectionalMTV(poly2, poly1, wallDir2);
  const leftAmount2 =
    leftMTV2 > 0 ? MATH_MIN(leftMTV2 + buffer, MAX_PUSH_DISTANCE) : 0;
  const rightAmount2 =
    rightMTV2 > 0 ? MATH_MIN(rightMTV2 + buffer, MAX_PUSH_DISTANCE) : 0;

  // Legacy fields for backward compatibility with tryPushToResolveCollision
  const pushDirection1 =
    leftAmount1 <= rightAmount1 && leftAmount1 > 0 ? -1 : 1;
  const pushDistance1 = pushDirection1 === -1 ? leftAmount1 : rightAmount1;
  const pushDirection2 =
    leftAmount2 <= rightAmount2 && leftAmount2 > 0 ? -1 : 1;
  const pushDistance2 = pushDirection2 === -1 ? leftAmount2 : rightAmount2;

  return {
    pushDistance1,
    pushDirection1,
    pushDistance2,
    pushDirection2,
    leftAmount1,
    rightAmount1,
    leftAmount2,
    rightAmount2,
  };
}

/**
 * Try to push a fixture along its wall
 */
function tryPushFixture(layout, fixtureIndex, distance, direction, bathroom) {
  const fixture = layout.fixtures[fixtureIndex];
  const wall = bathroom.walls.find((w) => w.id === fixture.parentWallIds?.[0]);

  if (!wall) {
    return { success: false, layout, failureReason: 'Wall not found' };
  }

  // Calculate wall direction
  const wallDir = getWallDirection(wall);

  // Calculate push amount
  const pushAmount = distance * direction;

  // Get current position and widths from corners
  const currentPos = getFixturePositionAlongWall(fixture.physicalCorners, wall);
  const newPosAlongWall = currentPos + pushAmount;
  const physicalWidth = getFixtureWidth(fixture.physicalCorners);
  const clearanceWidth = getFixtureWidth(fixture.clearanceCorners);
  const fixtureHalfWidth = MATH_MAX(physicalWidth, clearanceWidth) / 2;

  if (newPosAlongWall - fixtureHalfWidth < 0) {
    return {
      success: false,
      layout,
      failureReason: 'Would go past wall start',
    };
  }
  if (newPosAlongWall + fixtureHalfWidth > wall.length) {
    return { success: false, layout, failureReason: 'Would go past wall end' };
  }

  // Calculate new corners by shifting
  const newPhysicalCorners = shiftCornersAlongWall(
    fixture.physicalCorners,
    pushAmount,
    wallDir
  );
  const newClearanceCorners = shiftCornersAlongWall(
    fixture.clearanceCorners,
    pushAmount,
    wallDir
  );

  // Check for doors on the wall that would block
  if (wouldBlockDoor(newPhysicalCorners, wall, bathroom)) {
    return { success: false, layout, failureReason: 'Would block door' };
  }

  // Create new layout with pushed fixture
  const newLayout = cloneLayout(layout);
  const newFixture = newLayout.fixtures[fixtureIndex];

  // Update corners
  newFixture.physicalCorners = newPhysicalCorners;
  newFixture.clearanceCorners = newClearanceCorners;
  if (Array.isArray(newFixture.clearanceFootprints)) {
    newFixture.clearanceFootprints = newFixture.clearanceFootprints.map((fp) =>
      shiftCornersAlongWall(fp, pushAmount, wallDir)
    );
  }

  return { success: true, layout: newLayout };
}

/**
 * Check if fixture corners at the given position would block a door
 */
function wouldBlockDoor(physicalCorners, wall, bathroom) {
  const halfWidth = getFixtureWidth(physicalCorners) / 2;
  const fixturePosAlongWall = getFixturePositionAlongWall(
    physicalCorners,
    wall
  );

  // Check doors on this wall
  for (const door of bathroom.doors) {
    if (door.parentWallIds?.[0] !== wall.id) continue;

    const doorHalfWidth = (door.width || 0.8) / 2;
    const doorPosAlongWall = getPositionAlongWallLocal(door.center, wall);

    // Check if fixture overlaps with door
    if (
      MATH_ABS(fixturePosAlongWall - doorPosAlongWall) <
      halfWidth + doorHalfWidth
    ) {
      return true;
    }
  }

  return false;
}

/**
 * Get position of a point along a wall (local helper for door centers).
 */
function getPositionAlongWallLocal(point, wall) {
  const wallDir = getWallDirection(wall);
  const dx = point.x - wall.start.x;
  const dz = point.z - wall.start.z;
  return dx * wallDir.x + dz * wallDir.z;
}

// Floating-point noise epsilon: absorbs sub-mm drift from chained vector
// arithmetic. Distinct from caller-supplied semantic tolerance.
const FP_EPSILON = 1e-4; // 0.1 mm

/**
 * Compute the maximum signed distance `polygon` can be translated along
 * `direction` before it touches any obstacle in `obstacles`. Companion to
 * `computeDirectionalMTV`: same SAT machinery, opposite question.
 *
 *   computeDirectionalMTV: polygons currently OVERLAP — how far to push
 *     `polygon` along `direction` to separate them.
 *   sweptDistance:         polygon currently DOES NOT overlap — how far can
 *     it move along `direction` before it starts overlapping any obstacle.
 *
 * Algorithm (per obstacle):
 *   For each separating-axis candidate (edges of polygon and obstacle):
 *     1. Project both onto the axis.
 *     2. If they overlap on this axis, this axis isn't currently binding —
 *        translation along `direction` doesn't close a gap on it. Skip.
 *     3. If they're separated on this axis (gap > 0), compute how much
 *        translation along `direction` is needed to close that gap:
 *          s = direction · axis  (rate at which polygon's projection
 *                                 moves toward obstacle along this axis)
 *          t = gap / s            (when s > 0, polygon approaches; t < 0
 *                                 if moving away — axis never binds)
 *     4. The axis with smallest positive t binds first → that's the swept
 *        distance for this obstacle.
 *   Take min across obstacles.
 *
 * If the polygon and an obstacle are already overlapping (no separating
 * axis exists), returns 0 for that obstacle and the overall min is 0.
 * Caller can check `blocker` to detect this and route to MTV instead.
 *
 * @param {Object[]} polygon - Convex polygon corners [{x, z}, ...]
 * @param {{x: number, z: number}} direction - Unit vector
 * @param {Object[][]} obstacles - Array of convex polygons
 * @param {Object} [options]
 * @param {number} [options.tolerance=0] - Semantic separation tolerance
 *   (treats obstacles within tolerance as already touching).
 * @param {number} [options.maxDistance=Infinity] - Search ceiling
 * @returns {{ distance: number, blocker: Object[] | null }}
 *   distance: max travel along direction (>= 0)
 *   blocker:  the obstacle that bound the distance (null if no obstacles
 *             or no contact within maxDistance)
 */
function sweptDistance(polygon, direction, obstacles, options = {}) {
  const { tolerance = 0, maxDistance = Infinity } = options;
  let minDistance = maxDistance;
  let blocker = null;

  for (const obstacle of obstacles) {
    const t = sweptDistanceVsObstacle(polygon, direction, obstacle, tolerance);
    if (t < minDistance) {
      minDistance = t;
      blocker = obstacle;
      if (minDistance <= 0) {
        // Already touching/overlapping — can't move at all
        return { distance: 0, blocker };
      }
    }
  }

  return { distance: minDistance, blocker };
}

/**
 * Swept distance against a single obstacle (helper). Returns the maximum t
 * such that `polygon + t*direction` does not overlap `obstacle`, or 0 if
 * already overlapping.
 *
 * Standard swept-SAT: compute per-axis [t_enter, t_exit] intervals (when
 * projections overlap on each axis). Polygons overlap in 2D iff every axis
 * is non-separating; first-contact time = max(t_enter). If
 * max(t_enter) > min(t_exit), the per-axis windows don't all align and the
 * polygons never collide along this direction.
 *
 * Touching (gap = 0) classifies as separated with t_enter = 0 — toward
 * motion gives 0, away motion is rejected by the s ≤ 0 early-return.
 *
 * Sub-FP_EPSILON overlap on an axis is treated as touching (separated with
 * t_enter clamped to 0). This matches `overlapAmount`'s noise-floor behavior
 * so the two predicates agree: if `overlapAmount` reports 0 overlap, swept
 * distance never falls into the "currently overlapping in 2D" branch.
 *
 * Tolerance is no-op (kept for API compatibility).
 */
function sweptDistanceVsObstacle(polygon, direction, obstacle, tolerance) {
  const edges = [...getEdges(polygon), ...getEdges(obstacle)];
  let maxEnter = 0;
  let minExit = Infinity;
  let separatingAtT0 = false;

  for (const edge of edges) {
    const len = MATH_SQRT(edge.x * edge.x + edge.z * edge.z);
    if (len < 1e-10) continue;
    const axis = { x: -edge.z / len, z: edge.x / len };

    const proj1 = projectPolygon(polygon, axis);
    const proj2 = projectPolygon(obstacle, axis);
    const s = direction.x * axis.x + direction.z * axis.z;

    let tEnter;
    let tExit;

    if (proj1.max <= proj2.min + FP_EPSILON) {
      // poly1 entirely "left" of poly2 on this axis at t=0 (or touching /
      // sub-epsilon overlap — treated as touching so we agree with
      // overlapAmount's noise floor).
      separatingAtT0 = true;
      if (s <= FP_EPSILON) {
        // Moving parallel to or away from obstacle on this axis → axis stays
        // separating forever → no 2D collision along `direction`.
        return Infinity;
      }
      tEnter = MATH_MAX(0, (proj2.min - proj1.max) / s);
      tExit = (proj2.max - proj1.min) / s;
    } else if (proj1.min >= proj2.max - FP_EPSILON) {
      // poly1 entirely "right" of poly2 on this axis at t=0 (or touching /
      // sub-epsilon overlap).
      separatingAtT0 = true;
      if (s >= -FP_EPSILON) {
        return Infinity;
      }
      tEnter = MATH_MAX(0, (proj2.max - proj1.min) / s);
      tExit = (proj2.min - proj1.max) / s;
    } else {
      // Currently overlapping on this axis. Compute when the overlap
      // ends (axis becomes separating). Until then, this axis doesn't
      // constrain — t_enter = -Infinity (don't push maxEnter up).
      tEnter = -Infinity;
      if (MATH_ABS(s) <= FP_EPSILON) {
        tExit = Infinity; // overlap maintained forever along direction
      } else if (s > 0) {
        // proj1 shifts in +axis; leaves overlap when proj1.min > proj2.max
        tExit = (proj2.max - proj1.min) / s;
      } else {
        // proj1 shifts in -axis; leaves overlap when proj1.max < proj2.min
        tExit = (proj2.min - proj1.max) / s;
      }
    }

    if (tEnter > maxEnter) maxEnter = tEnter;
    if (tExit < minExit) minExit = tExit;
  }

  if (!separatingAtT0) return 0; // already overlapping in 2D
  if (maxEnter > minExit) return Infinity; // per-axis windows don't align
  if (maxEnter < FP_EPSILON) return 0; // sub-noise = effectively touching
  return maxEnter;
}

/**
 * Compute static overlap amount between two convex polygons (positive if
 * overlapping, 0 if separated). Used by callers that need a magnitude rather
 * than a boolean. Same SAT machinery as `doPolygonsOverlap` but returns the
 * minimum overlap distance across all separating axes (the MTV magnitude
 * for the not-yet-pushed case).
 *
 * @param {Object[]} poly1 - Convex polygon corners
 * @param {Object[]} poly2 - Convex polygon corners
 * @param {Object} [options]
 * @param {number} [options.tolerance=0] - Overlaps below tolerance treated as 0
 * @returns {number} Overlap magnitude (>= 0; 0 if separated within tolerance)
 */
function overlapAmount(poly1, poly2, options = {}) {
  const { tolerance = 0 } = options;
  const edges = [...getEdges(poly1), ...getEdges(poly2)];
  let minOverlap = Infinity;

  for (const edge of edges) {
    const len = MATH_SQRT(edge.x * edge.x + edge.z * edge.z);
    if (len < 1e-10) continue;
    const axis = { x: -edge.z / len, z: edge.x / len };

    const p1 = projectPolygon(poly1, axis);
    const p2 = projectPolygon(poly2, axis);

    // Separated on this axis (within tolerance) → no overlap
    if (p1.max <= p2.min + tolerance || p2.max <= p1.min + tolerance) {
      return 0;
    }

    const overlap = MATH_MIN(p1.max, p2.max) - MATH_MAX(p1.min, p2.min);
    if (overlap < minOverlap) minOverlap = overlap;
  }

  // Apply FP epsilon: sub-noise overlap is treated as 0
  if (minOverlap < FP_EPSILON) return 0;
  return MATH_MAX(0, minOverlap - tolerance);
}

module.exports = {
  resolvePhysicalCollisions,
  calculateOverlapInfo,
  tryPushFixture,
  calculateMTV,
  computeDirectionalMTV,
  sweptDistance,
  overlapAmount,
  FP_EPSILON,
};
